import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Layout from '../components/layout/Layout';
import { CreatePaydleForm } from '../components/Paydle/CreatePaydleForm';
import { useAuth } from '../hooks/useAuth';

const CreatePaydle: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [showSuccess, setShowSuccess] = useState(false);
  const [shareId, setShareId] = useState('');
  const [accessToken, setAccessToken] = useState('');

  // Redirect to sign-up if not authenticated
  useEffect(() => {
    if (currentUser === null) {
      navigate('/sign-up');
    }
  }, [currentUser, navigate]);

  const handleSuccess = (paydleId: string, newShareId: string, newAccessToken: string) => {
    setShareId(newShareId);
    setAccessToken(newAccessToken);
    setShowSuccess(true);
  };

  const handleViewDashboard = () => {
    navigate(`/paydle/manage/${accessToken}`);
  };

  const copyShareLink = () => {
    const shareUrl = `${window.location.origin}/paydle/play/${shareId}`;
    navigator.clipboard.writeText(shareUrl);
    alert('Share link copied to clipboard!');
  };

  // Don't render anything if not authenticated (will redirect)
  if (!currentUser) {
    return null;
  }

  if (showSuccess) {
    return (
      <Layout>
        <div className="bg-gradient-to-br from-green-50 to-blue-50 py-12 px-4">
          <div className="max-w-2xl mx-auto">
            <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg shadow-xl p-8"
          >
            <div className="text-center mb-6">
              <div className="text-6xl mb-4">🎉</div>
              <h2 className="text-3xl font-bold text-green-600 mb-2">
                Paydle Created Successfully!
              </h2>
              <p className="text-gray-600">
                Your word puzzle is ready to share. Players who fail will pay up!
              </p>
            </div>

            <div className="bg-blue-50 rounded-lg p-6 mb-6">
              <h3 className="font-bold text-gray-700 mb-3">Share this link with players:</h3>
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={`${window.location.origin}/paydle/play/${shareId}`}
                  readOnly
                  className="flex-1 px-4 py-2 border border-gray-300 rounded bg-white"
                />
                <button
                  onClick={copyShareLink}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-semibold transition-colors"
                >
                  Copy
                </button>
              </div>
            </div>

            <div className="bg-yellow-50 rounded-lg p-6 mb-6 border-2 border-yellow-200">
              <h3 className="font-bold text-gray-700 mb-2">Important - Save Your Dashboard Link!</h3>
              <p className="text-sm text-gray-600 mb-3">
                Use this link to view attempts and manage your Paydle:
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={`${window.location.origin}/paydle/manage/${accessToken}`}
                  readOnly
                  className="flex-1 px-3 py-2 border border-gray-300 rounded bg-white text-sm"
                />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(
                      `${window.location.origin}/paydle/manage/${accessToken}`
                    );
                    alert('Dashboard link copied!');
                  }}
                  className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded font-semibold transition-colors"
                >
                  Copy
                </button>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleViewDashboard}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-lg transition-colors"
              >
                View Dashboard
              </button>
              <button
                onClick={() => navigate('/')}
                className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Go Home
              </button>
            </div>
          </motion.div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="bg-gradient-to-br from-purple-50 to-blue-50 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-5xl font-extrabold text-gray-800 mb-4">
            <span className="bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
              Create a Paydle
            </span>
          </h1>
          <p className="text-xl text-gray-600">
            Challenge someone to guess your word - or make them pay!
          </p>
        </motion.div>

        <CreatePaydleForm onSuccess={handleSuccess} onCancel={() => navigate('/')} />
        </div>
      </div>
    </Layout>
  );
};

export default CreatePaydle;
